import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import DataTable from '../../../components/orizon-ui/DataTable';
import SearchInput from '../../../components/orizon-ui/SearchInput';
import StatusPill from '../../../components/orizon-ui/StatusPill';
import { useToast } from '../../../components/orizon-ui/Toast';
import { useConfirm } from '../../../components/orizon-ui/ConfirmDialog';
import { gruElimina, gruImpostaAttivo, mapGruError } from '../../../lib/services/gruService';
import { useListaGru } from '../../../components/gru/useListaGru';

/** Colonna "Stato" comune a tutte le liste */
export const colonnaAttivo = {
  key: 'attivo',
  label: 'Stato',
  width: 120,
  align: 'center',
  render: (r) => <StatusPill tono={r.attivo ? 'ok' : 'neutro'} label={r.attivo ? 'Attivo' : 'Disattivato'} />,
};

const TONI_AZIONE = {
  neutro:  'bg-muted/30 border-border/40 text-muted-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary',
  warn:    'bg-muted/30 border-border/40 text-muted-foreground hover:bg-amber-500/10 hover:text-amber-500 hover:border-amber-500/30',
  ok:      'bg-muted/30 border-border/40 text-muted-foreground hover:bg-emerald-500/10 hover:text-emerald-500 hover:border-emerald-500/30',
  pericolo:'bg-muted/30 border-border/40 text-muted-foreground hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/30',
};

const AzioneBtn = ({ icona, titolo, onClick, tono = 'neutro', disabilitato = false }) => (
  <button
    type="button"
    title={titolo}
    aria-label={titolo}
    disabled={disabilitato}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className={`w-9 h-9 rounded-xl border flex items-center justify-center transition-all disabled:opacity-40 disabled:cursor-not-allowed ${TONI_AZIONE[tono]}`}
  >
    <Icon name={icona} size={15} />
  </button>
);

const etichettaPredefinita = (r) => r.label || r.nome || r.codice || r.etichetta || r.gru_codice || 'questo elemento';

/**
 * Sezione di configurazione: toolbar (ricerca, mostra disattivati, nuovo) + tabella
 * paginata lato server + azioni (modifica, attiva/disattiva, elimina) + form in modal.
 *
 * Props:
 *  entita, titolo, descrizione, nuovoLabel
 *  colonne            colonne dati (le azioni vengono aggiunte qui)
 *  ordineIniziale     { key, direction }
 *  filtri             filtri uguaglianza lato server (es. { reparto_id })
 *  FormComponent      componente del form: ({ record, filtri, onClose, onSaved, ...formProps })
 *  formProps          props extra per il form
 *  labelRiga(riga)    testo usato nelle conferme
 *  onRigaClick, rigaSelezionataId   per liste "master"
 *  toolbarExtra       nodo aggiuntivo nella toolbar
 *  compatta           titolo più piccolo (per le liste affiancate)
 */
const SezioneConfig = ({
  entita,
  titolo,
  descrizione,
  nuovoLabel = 'Nuovo',
  colonne,
  ordineIniziale,
  filtri,
  FormComponent,
  formProps = {},
  labelRiga = etichettaPredefinita,
  onRigaClick,
  rigaSelezionataId,
  toolbarExtra,
  compatta = false,
  perPagina = 25,
  messaggioVuoto,
}) => {
  const toast = useToast();
  const confirm = useConfirm();
  const [modale, setModale] = useState(null); // null | { record: oggetto | null }
  const [inCorso, setInCorso] = useState(null); // id della riga su cui si sta operando

  const lista = useListaGru({ entita, filtri, ordineIniziale, perPagina });

  const cambiaAttivo = async (riga, attivo) => {
    if (!attivo) {
      const ok = await confirm({
        titolo: 'Disattivare l\'elemento?',
        descrizione: `"${labelRiga(riga)}" non sarà più selezionabile, ma i dati collegati restano.`,
        confermaLabel: 'Disattiva',
        variante: 'primary',
      });
      if (!ok) return;
    }
    setInCorso(riga.id);
    try {
      await gruImpostaAttivo(entita, riga.id, attivo);
      toast.success(attivo ? 'Elemento riattivato.' : 'Elemento disattivato.');
      lista.ricarica();
    } catch (e) {
      toast.error(mapGruError(e).message);
    } finally {
      setInCorso(null);
    }
  };

  const elimina = async (riga) => {
    const ok = await confirm({
      titolo: 'Eliminare definitivamente?',
      descrizione: `"${labelRiga(riga)}" verrà eliminato. Se è collegato ad altri dati non sarà possibile.`,
      confermaLabel: 'Elimina',
    });
    if (!ok) return;

    setInCorso(riga.id);
    try {
      await gruElimina(entita, riga.id);
      toast.success('Elemento eliminato.');
      lista.ricarica();
    } catch (e) {
      const err = mapGruError(e);
      if (err.code === 'RECORD_IN_USO' && riga.attivo) {
        // elemento collegato: proponi la disattivazione al posto dell'eliminazione
        const disattiva = await confirm({
          titolo: 'Elemento in uso',
          descrizione: `${err.message} Vuoi disattivare "${labelRiga(riga)}"?`,
          confermaLabel: 'Disattiva',
          variante: 'primary',
        });
        if (disattiva) {
          try {
            await gruImpostaAttivo(entita, riga.id, false);
            toast.success('Elemento disattivato.');
            lista.ricarica();
          } catch (e2) {
            toast.error(mapGruError(e2).message);
          }
        }
      } else {
        toast.error(err.message);
      }
    } finally {
      setInCorso(null);
    }
  };

  const colonneConAzioni = [
    ...colonne,
    {
      key: '__azioni',
      label: '',
      width: 150,
      align: 'center',
      noSort: true,
      render: (riga) => (
        <div className="flex items-center justify-center gap-2">
          <AzioneBtn icona="Pencil" titolo="Modifica" onClick={() => setModale({ record: riga })} disabilitato={inCorso === riga.id} />
          {riga.attivo ? (
            <AzioneBtn icona="PowerOff" titolo="Disattiva" tono="warn" onClick={() => cambiaAttivo(riga, false)} disabilitato={inCorso === riga.id} />
          ) : (
            <AzioneBtn icona="Power" titolo="Riattiva" tono="ok" onClick={() => cambiaAttivo(riga, true)} disabilitato={inCorso === riga.id} />
          )}
          <AzioneBtn icona="Trash2" titolo="Elimina" tono="pericolo" onClick={() => elimina(riga)} disabilitato={inCorso === riga.id} />
        </div>
      ),
    },
  ];

  return (
    <section className="flex flex-col gap-4 min-w-0">
      {/* Titolo sezione */}
      <div className="flex flex-col gap-1 px-1">
        <h3 className={`${compatta ? 'text-lg' : 'text-xl'} font-black uppercase tracking-tight text-foreground`}>{titolo}</h3>
        {descrizione && (
          <p className="text-[10px] font-black uppercase tracking-[.2em] text-muted-foreground/50">{descrizione}</p>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 md:gap-3">
        <SearchInput value={lista.ricercaInput} onChange={lista.setRicercaInput} placeholder="Cerca..." />

        <button
          type="button"
          onClick={() => lista.setMostraDisattivati(!lista.mostraDisattivati)}
          className={`flex items-center gap-2 px-3 md:px-4 py-2.5 border rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all duration-200 shadow-sm ${
            lista.mostraDisattivati
              ? 'bg-primary/10 border-primary/30 text-primary hover:bg-primary/20'
              : 'bg-muted/40 hover:bg-muted/80 border-border/40 hover:border-primary/30 text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name={lista.mostraDisattivati ? 'Eye' : 'EyeOff'} size={14} />
          <span className="hidden sm:inline">Disattivati</span>
        </button>

        {toolbarExtra}

        <button
          type="button"
          onClick={() => setModale({ record: null })}
          className="flex items-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-2xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 group active:scale-95"
        >
          <Icon name="Plus" size={18} className="group-hover:rotate-90 transition-transform duration-300" />
          <span className="text-[10px] font-black uppercase tracking-[.2em]">{nuovoLabel}</span>
        </button>
      </div>

      <DataTable
        colonne={colonneConAzioni}
        righe={lista.righe}
        totale={lista.totale}
        loading={lista.loading}
        pagina={lista.pagina}
        perPagina={lista.perPagina}
        onPagina={lista.setPagina}
        sort={lista.sort}
        onSort={lista.onSort}
        onRigaClick={onRigaClick}
        rigaSelezionataId={rigaSelezionataId}
        rigaDisattivata={(r) => r.attivo === false}
        messaggioVuoto={messaggioVuoto || 'Nessun elemento trovato'}
        altezzaMax={compatta ? 'max-h-[52dvh]' : 'max-h-[62dvh]'}
      />

      {modale && (
        <FormComponent
          record={modale.record}
          filtri={filtri}
          {...formProps}
          onClose={() => setModale(null)}
          onSaved={() => {
            setModale(null);
            lista.ricarica();
          }}
        />
      )}
    </section>
  );
};

export default SezioneConfig;
